import { useEffect, useRef, useState } from "react";
import type { Member, MeetingZone, Suggestion } from "../api/types";
import { loadMapsLibrary } from "../googleMaps";

interface Props {
  zone: MeetingZone;
  venues: Suggestion[];
  members: Member[];
  apiKey: string;
}

/**
 * Build a curved arc between two points.
 * `side` flips the curve direction (1 = left, -1 = right).
 */
function curvedPath(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  side = 1,
  steps = 50,
): { lat: number; lng: number }[] {
  const dLat = to.lat - from.lat;
  const dLng = to.lng - from.lng;
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);
  if (dist < 0.0001) return [from, to];
  const midLat = (from.lat + to.lat) / 2;
  const midLng = (from.lng + to.lng) / 2;
  const bulge = dist * 0.22;
  const nx = (-dLng / dist) * side;
  const ny = (dLat / dist) * side;
  const cpLat = midLat + nx * bulge;
  const cpLng = midLng + ny * bulge;

  const points: { lat: number; lng: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    points.push({
      lat: u * u * from.lat + 2 * u * t * cpLat + t * t * to.lat,
      lng: u * u * from.lng + 2 * u * t * cpLng + t * t * to.lng,
    });
  }
  return points;
}

// Bright, high-contrast colors that stand out on the map
const MEMBER_COLORS = [
  "#1B8C3A", "#D63B0F", "#1565C0", "#8E24AA",
  "#E65100", "#00838F", "#C62828", "#2E7D32",
  "#6A1B9A", "#00695C",
];

export function MeetingMap({ zone, venues, members, apiKey }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    const mobile = window.innerWidth < 480;

    loadMapsLibrary(apiKey)
      .then(() => window.google!.maps!.importLibrary!("marker"))
      .then(() => {
        if (cancelled || !containerRef.current) return;

        const map = new google.maps.Map(containerRef.current, {
          mapId: "DEMO_MAP_ID",
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        });

        // Zone polygon (subtle fill, drawn first so lines sit on top)
        if (zone.polygon) {
          map.data.addGeoJson({
            type: "Feature",
            geometry: zone.polygon,
            properties: {},
          });
          map.data.setStyle({
            fillColor: "#0e6e62",
            fillOpacity: 0.08,
            strokeColor: "#0e6e62",
            strokeWeight: 1,
            strokeOpacity: 0.4,
          });
        }

        const bounds = new google.maps.LatLngBounds();

        // Venue markers
        let openInfo: google.maps.InfoWindow | null = null;
        for (const suggestion of venues) {
          const { venue } = suggestion;
          const position = { lat: venue.point.lat, lng: venue.point.lng };
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            title: venue.name,
          });
          bounds.extend(position);

          const perMemberHtml = suggestion.per_member
            .map((m) => `<div>${m.name}: ${Math.round(m.travel_minutes)} min</div>`)
            .join("");
          const info = new google.maps.InfoWindow({
            content:
              `<div style="font-family:system-ui;font-size:${mobile ? "15" : "13"}px">` +
              `<strong>${venue.name}</strong>` +
              `<div style="color:#6b7873;font-size:${mobile ? "13" : "11"}px;margin:2px 0">${venue.kind}</div>` +
              perMemberHtml +
              `</div>`,
          });
          (marker as unknown as HTMLElement).addEventListener("gmp-click", () => {
            openInfo?.close();
            info.open({ map, anchor: marker });
            openInfo = info;
          });
        }

        // Member markers (bold colored circles with initials)
        for (let mi = 0; mi < members.length; mi++) {
          const member = members[mi]!;
          const position = { lat: member.point.lat, lng: member.point.lng };
          const color = MEMBER_COLORS[mi % MEMBER_COLORS.length] ?? "#1B8C3A";

          const pin = document.createElement("div");
          const sz = mobile ? 38 : 30;
          const fs = mobile ? 15 : 13;
          pin.style.cssText =
            `width:${sz}px;height:${sz}px;border-radius:50%;background:${color};` +
            `color:#fff;font-weight:700;font-size:${fs}px;display:flex;` +
            "align-items:center;justify-content:center;border:3px solid #fff;" +
            "box-shadow:0 2px 8px rgba(0,0,0,.5);";
          pin.textContent = member.name.charAt(0).toUpperCase();

          new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            title: member.name,
            content: pin,
            zIndex: 10,
          });
          bounds.extend(position);
        }

        map.fitBounds(bounds, 48);

        // ── Route lines: member → centroid ──
        // Drawn last so they render on top of everything.
        // Each member gets a bold curved arrow line to the fair meeting point.
        const target = { lat: zone.centroid.lat, lng: zone.centroid.lng };

        for (let mi = 0; mi < members.length; mi++) {
          const member = members[mi]!;
          const from = { lat: member.point.lat, lng: member.point.lng };
          const color = MEMBER_COLORS[mi % MEMBER_COLORS.length] ?? "#1B8C3A";
          // Alternate curve direction so lines from different sides don't overlap
          const side = mi % 2 === 0 ? 1 : -1;
          const path = curvedPath(from, target, side);

          // White outline underneath for contrast on any background
          const outline = new google.maps.Polyline({
            path,
            geodesic: false,
            strokeColor: "#ffffff",
            strokeOpacity: 0.6,
            strokeWeight: 6,
          });
          outline.setMap(map);

          // Colored line on top with repeating arrows
          const line = new google.maps.Polyline({
            path,
            geodesic: false,
            strokeColor: color,
            strokeOpacity: 1.0,
            strokeWeight: 3.5,
            icons: [
              {
                icon: {
                  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 4,
                  strokeColor: color,
                  strokeWeight: 0.5,
                  fillColor: color,
                  fillOpacity: 1,
                },
                repeat: "60px",
                offset: "30px",
              },
            ],
          });
          line.setMap(map);
        }
      })
      .catch((err) => {
        console.error("[MeetingMap] failed to load:", err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, zone, venues, members]);

  if (failed) {
    return (
      <div className="meeting-map-error muted small">
        Map could not load. Check the browser console for details.
      </div>
    );
  }

  return <div ref={containerRef} className="meeting-map" />;
}
